import React from 'react';
import { Compass, BarChart2, BookOpen, Boxes, ClipboardList, Shield, ArrowRight, Sparkles, Crown, Tag } from 'lucide-react';
import researchersImage from '../../assets/researchers.jpg';
import surveyImage from '../../assets/researchersimage3.jpg';
import './LandingPage.css';

const LandingPage = ({ onGetStarted, onUpgrade }) => {
  const wizards = [
    {
      icon: Compass,
      letter: 'A',
      title: 'Research Gap Finder',
      color: '#0d9488',
      description: 'Analyse 10–15 scholarly article abstracts to automatically detect six gap types: Theoretical, Empirical, Methodological, Contextual, Contradictory, and Practical. We build a Limitations Matrix with charts, score your gap\'s validity, generate a gap formula, and produce a full 300+ word gap statement — ready to paste into your literature review.'
    },
    {
      icon: BookOpen,
      letter: 'B',
      title: 'Research Methodology',
      color: '#4f46e5',
      description: 'We guide you to choose key research question types and make your research choices using plain language. We\'ll use your answers to recommend the best research methodology (including dimensions such as research philosophy, ontology, epistemology and axiology). Don\'t worry, we explain these terms and give examples!'
    },
    {
      icon: BarChart2,
      letter: 'C',
      title: 'Data Analysis Planning',
      color: '#0891b2',
      description: 'Plan your data analysis strategy BEFORE collecting data. Choose from qualitative methods (thematic analysis, sentiment analysis) and/or quantitative methods (descriptive statistics, inferential tests). We help you understand what data types each analysis requires so your survey questions match your analysis plan.'
    },
    {
      icon: Boxes,
      letter: 'D',
      title: 'Framework Builder',
      color: '#7c3aed',
      description: 'We teach you the fundamentals of creating a conceptual framework. Our builder helps you identify and map your main research constructs as dependent, independent, mediating, moderating and control variables, and align the theories that work best for your research. Outputs include a downloadable framework diagram (PNG) for your methodology chapter.'
    },
    {
      icon: ClipboardList,
      letter: 'E',
      title: 'Data Collection Process',
      color: '#059669',
      description: 'We help you understand all the main data collection processes, with tips, best practices and examples so that you know exactly what data you need and in what format — and HOW to collect it efficiently and ethically. Proper data collection ensures your methods align with your aims, reduces error, and improves reliability.'
    },
    {
      icon: Shield,
      letter: 'F',
      title: 'Ethics Approval',
      color: '#dc2626',
      description: 'Our Ethics Approval wizard is a dry-run regardless of whether your institution has an ethics committee or not. Walk through eligibility, participant information, consent, data management, and risk assessment. Outputs a full Ethics Report customised for your research — strengthening validity and demonstrating accountability.'
    },
    {
      icon: BarChart2,
      letter: 'G',
      title: 'Survey Builder & Qualitative Analysis',
      color: '#0ea5e9',
      description: 'Build professional survey questionnaires with multiple question types (Likert scales, multiple choice, open-ended). Upload qualitative data documents (interview transcripts, focus group notes) in TXT, DOCX, PDF, or Excel formats. Analyse text using 7 powerful methods including Thematic Analysis, Sentiment Analysis, Grounded Theory, and Phenomenological Analysis.'
    }
  ];

  return (
    <div className="landing-page">
      <div className="landing-hero">
        <div className="hero-header">
          <div className="hero-sparkle">
            <Sparkles size={28} />
          </div>
          <h1 className="landing-title">Welcome to Research Methodology Wizard</h1>
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <p className="landing-subtitle">
              For those involved in research (Final Year Bachelor degree, Masters degree, PhD degree, PostDoctorate Researchers and early career or inexperienced academics/faculty members) getting your research methodology sorted with the right questions, conceptual framework, methods, data types, theoretical framework and data analysis could take several months.
            </p>
            <p className="landing-subtitle">
              Our research wizard has simplified this process into something do-able in a matter of weeks - with the opportunity to create many research designs and compare them before adopting the most ideal. Using a combination of various "Wizards" we guide or teach you about making the right methodological choices. We make research methodology fun!
            </p>
            <div className="hero-cta-buttons">
              <button className="landing-cta" onClick={onGetStarted}>
                Get Started <ArrowRight size={20} />
              </button>
              <button className="landing-cta-pricing" onClick={onUpgrade}>
                <Tag size={18} /> Pricing
              </button>
            </div>
          </div>
          <div className="hero-images">
            <div className="hero-image">
              <img src={researchersImage} alt="Researchers collaborating on methodology" />
            </div>
            <div className="hero-image">
              <img src={surveyImage} alt="Survey and data collection" />
            </div>
          </div>
        </div>
      </div>

      <div className="wizards-showcase">
        <h2 className="showcase-title">What You'll Discover</h2>
        <div className="wizards-grid">
          {wizards.map((wizard) => {
            const IconComponent = wizard.icon;
            return (
              <div key={wizard.letter} className="wizard-showcase-card" style={{ '--accent-color': wizard.color }}>
                <div className="wizard-card-header">
                  <div className="wizard-icon-wrapper" style={{ backgroundColor: wizard.color }}>
                    <IconComponent size={24} />
                  </div>
                  <div className="wizard-letter-badge">Wizard {wizard.letter}</div>
                </div>
                <h3 className="wizard-card-title">{wizard.title}</h3>
                <p className="wizard-card-description">{wizard.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="landing-cta-section">
        <div className="cta-card">
          <div className="cta-icon">
            <Crown size={32} />
          </div>
          <h3>Did you know?</h3>
          <p>
            You can create many research design methodologies and compare them side by side? 
            Upgrade to <strong>Advanced User</strong> and unlock everything this wonderful tool has to offer.
          </p>
          <p className="cta-highlight">
            Creating/Writing a research methodology section/chapter is no longer a nightmare: from several months of struggling with theories, concepts, methods, data, etc. — you can have a solution in a matter of days or weeks — and you can keep saving and refining your methodology until you (and your supervisor/ethics committee) are satisfied.
          </p>
          <div className="cta-buttons">
            <button className="btn-primary" onClick={onGetStarted}>
              Start with Free Version <ArrowRight size={18} />
            </button>
            <button className="btn-secondary" onClick={onUpgrade}>
              <Crown size={18} /> View Upgrade Options
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
